import React, { useState, useEffect, useRef } from 'react';
import { Mic, Plus, Trash2, Edit, ChevronDown, ChevronUp, Eye, Save, Upload, Volume2, Loader2 } from 'lucide-react';
import { contentAPI, getMediaUrl } from '../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const tacheDescriptions = {
  1: 'Tâche 1 : Entretien dirigé (2 min) - Répondre à des questions sur soi-même',
  2: 'Tâche 2 : Exercice en interaction (5 min) - Dialogue simulé avec un interlocuteur',
  3: 'Tâche 3 : Expression d\'un point de vue (5 min) - Donner son opinion sur un sujet'
};

const tacheTypes = {
  1: 'Entretien dirigé',
  2: 'Interaction',
  3: 'Point de vue'
};

// TacheForm component defined OUTSIDE to prevent re-creation on every render
const TacheForm = ({ 
  tacheNum, 
  tacheKey, 
  formData, 
  setFormData, 
  fileInputRef,
  handleAudioUpload,
  handleGenerateTTS,
  generatingTTS,
  blocId
}) => {
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [tacheKey]: { ...prev[tacheKey], [field]: value }
    }));
  };

  return (
    <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-orange-600 dark:text-orange-400">Tâche {tacheNum} - {tacheTypes[tacheNum]}</h4>
        <span className="text-xs text-muted-foreground">{formData[tacheKey].duree} min {formData[tacheKey].dureeSecondes || 0}s</span>
      </div>
      <p className="text-xs text-muted-foreground">{tacheDescriptions[tacheNum]}</p>
      
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">Titre du sujet</label>
          <input
            type="text"
            value={formData[tacheKey].titre}
            onChange={(e) => handleFieldChange('titre', e.target.value)}
            className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
            placeholder={`Ex: ${tacheTypes[tacheNum]}`}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Consigne / Instruction *</label>
          <textarea
            value={formData[tacheKey].instruction}
            onChange={(e) => handleFieldChange('instruction', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm min-h-[60px] mt-1"
            placeholder="Ex: L'examinateur vous pose des questions sur vous-même..."
            required
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Sujet / Contexte *</label>
          <textarea
            value={formData[tacheKey].sujet}
            onChange={(e) => handleFieldChange('sujet', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm min-h-[80px] mt-1"
            placeholder="Décrivez le contexte ou le scénario..."
            required
          />
        </div>

        {/* Modèle de correction - AUDIO */}
        <div className="border-t pt-4 mt-4">
          <label className="text-sm font-medium text-green-600 dark:text-green-400 block mb-3">
            Modèle de correction (Audio)
          </label>
          
          {/* Option 1: Upload Audio */}
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800 mb-3">
            <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">
              Option 1 : Téléverser un fichier audio
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleAudioUpload(tacheKey, e.target.files[0], blocId);
                }
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
            >
              <Upload className="h-4 w-4" />
              <span>Choisir un fichier audio</span>
            </button>
          </div>

          {/* Option 2: Generate from Text */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
              Option 2 : Générer l'audio à partir du texte (IA)
            </p>
            <textarea
              value={formData[tacheKey].modeleTexte}
              onChange={(e) => handleFieldChange('modeleTexte', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border bg-background text-sm min-h-[80px] mt-1 mb-2"
              placeholder="Entrez le texte de l'exemple de réponse. L'IA le convertira en audio..."
            />
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => handleGenerateTTS(tacheKey, blocId)}
                disabled={generatingTTS[tacheKey] || !formData[tacheKey].modeleTexte}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingTTS[tacheKey] ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Génération...</span>
                  </>
                ) : (
                  <>
                    <Volume2 className="h-4 w-4" />
                    <span>Générer l'audio</span>
                  </>
                )}
              </button>
              <span className="text-xs text-muted-foreground">
                {formData[tacheKey].modeleTexte?.length || 0}/4096 caractères
              </span>
            </div>
          </div>

          {/* Audio Preview */}
          {formData[tacheKey].modeleAudioUrl && (
            <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">Audio généré :</p>
              <audio controls controlsList="nodownload" className="w-full h-10">
                <source src={getMediaUrl(formData[tacheKey].modeleAudioUrl)} type="audio/mpeg" />
              </audio>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="text-xs font-medium">Durée (min)</label>
            <input
              type="number"
              value={formData[tacheKey].duree}
              onChange={(e) => handleFieldChange('duree', parseInt(e.target.value) || 0)}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium">Durée (sec)</label>
            <input
              type="number"
              value={formData[tacheKey].dureeSecondes || 0}
              onChange={(e) => handleFieldChange('dureeSecondes', parseInt(e.target.value) || 0)}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
              max="59"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ExpressionOraleManager = () => {
  const [blocs, setBlocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBloc, setEditingBloc] = useState(null);
  const [expandedBloc, setExpandedBloc] = useState(null);
  const [generatingTTS, setGeneratingTTS] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    tache1: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 2, dureeSecondes: 0 },
    tache2: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 5, dureeSecondes: 0 },
    tache3: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 5, dureeSecondes: 0 }
  });

  const fileInputRefs = {
    tache1: useRef(null),
    tache2: useRef(null),
    tache3: useRef(null)
  };

  useEffect(() => {
    loadBlocs();
  }, []);

  const loadBlocs = async () => {
    try {
      const loadedBlocs = await contentAPI.getBlocs('expression_orale');
      setBlocs(Array.isArray(loadedBlocs) ? loadedBlocs : []);
    } catch (error) {
      console.error('Error loading blocs:', error);
      toast.error('Erreur lors du chargement des blocs');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      tache1: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 2, dureeSecondes: 0 },
      tache2: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 5, dureeSecondes: 0 },
      tache3: { titre: '', instruction: '', sujet: '', modeleAudioUrl: '', modeleTexte: '', duree: 5, dureeSecondes: 0 }
    });
  };

  const handleAudioUpload = async (tacheKey, file, blocId = 'temp') => {
    const tacheNum = parseInt(tacheKey.replace('tache', ''));
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/tts/${blocId}/${tacheNum}`, {
        method: 'POST',
        body: formDataUpload
      });
      
      if (response.ok) {
        const result = await response.json();
        setFormData(prev => ({
          ...prev,
          [tacheKey]: { ...prev[tacheKey], modeleAudioUrl: result.url }
        }));
        toast.success('Audio uploadé avec succès');
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleGenerateTTS = async (tacheKey, blocId = 'temp') => {
    const tacheNum = parseInt(tacheKey.replace('tache', ''));
    const texte = formData[tacheKey].modeleTexte;
    
    if (!texte || texte.trim().length === 0) {
      toast.error('Veuillez d\'abord entrer le texte du modèle');
      return;
    }
    
    if (texte.length > 4096) {
      toast.error('Le texte ne doit pas dépasser 4096 caractères');
      return;
    }
    
    setGeneratingTTS(prev => ({ ...prev, [tacheKey]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: texte,
          voice: 'nova',
          speed: 1.0,
          bloc_id: blocId,
          tache_number: tacheNum
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setFormData(prev => ({
          ...prev,
          [tacheKey]: { ...prev[tacheKey], modeleAudioUrl: result.url }
        }));
        toast.success('Audio généré avec succès !');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('TTS generation error:', error);
      toast.error('Erreur lors de la génération audio');
    } finally {
      setGeneratingTTS(prev => ({ ...prev, [tacheKey]: false }));
    }
  };

  const handleCreateBloc = async () => {
    try {
      const blocNumber = blocs.length + 1;
      const newBloc = {
        id: `eo_bloc_${Date.now()}`,
        name: formData.name || `Bloc ${blocNumber}`,
        number: blocNumber,
        tasks: [
          { ...formData.tache1, tache: 1 },
          { ...formData.tache2, tache: 2 },
          { ...formData.tache3, tache: 3 }
        ],
        created_at: new Date().toISOString()
      };
      
      const result = await contentAPI.createBloc('expression_orale', newBloc);
      if (result.success) {
        toast.success(`Bloc "${newBloc.name}" créé avec succès`);
        resetForm();
        setShowForm(false);
        loadBlocs();
      } else {
        toast.error(result.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating bloc:', error);
      toast.error('Erreur lors de la création du bloc');
    }
  };

  const handleUpdateBloc = async () => {
    try {
      const updates = {
        name: formData.name || editingBloc.name,
        tasks: [
          { ...formData.tache1, tache: 1 },
          { ...formData.tache2, tache: 2 },
          { ...formData.tache3, tache: 3 }
        ]
      };
      
      await contentAPI.updateBloc('expression_orale', editingBloc.id, updates);
      toast.success('Bloc mis à jour avec succès');
      setEditingBloc(null);
      resetForm();
      loadBlocs();
    } catch (error) {
      console.error('Error updating bloc:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteBloc = async (blocId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce bloc ?')) {
      try {
        await contentAPI.deleteBloc('expression_orale', blocId);
        toast.success('Bloc supprimé');
        loadBlocs();
      } catch (error) {
        console.error('Error deleting bloc:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleEditBloc = (bloc) => {
    setEditingBloc(bloc);
    const tasks = bloc.tasks || bloc.taches || [];
    const tache1 = tasks.find(t => t.tache === 1) || {};
    const tache2 = tasks.find(t => t.tache === 2) || {};
    const tache3 = tasks.find(t => t.tache === 3) || {};
    
    setFormData({
      name: bloc.name,
      tache1: { 
        titre: tache1.titre || '', 
        instruction: tache1.instruction || '', 
        sujet: tache1.sujet || '', 
        modeleAudioUrl: tache1.modeleAudioUrl || '', 
        modeleTexte: tache1.modeleTexte || '', 
        duree: tache1.duree || 2,
        dureeSecondes: tache1.dureeSecondes || 0
      },
      tache2: { 
        titre: tache2.titre || '', 
        instruction: tache2.instruction || '', 
        sujet: tache2.sujet || '', 
        modeleAudioUrl: tache2.modeleAudioUrl || '', 
        modeleTexte: tache2.modeleTexte || '', 
        duree: tache2.duree || 5,
        dureeSecondes: tache2.dureeSecondes || 0 
      },
      tache3: { 
        titre: tache3.titre || '', 
        instruction: tache3.instruction || '', 
        sujet: tache3.sujet || '', 
        modeleAudioUrl: tache3.modeleAudioUrl || '', 
        modeleTexte: tache3.modeleTexte || '', 
        duree: tache3.duree || 5,
        dureeSecondes: tache3.dureeSecondes || 0 
      }
    });
    setShowForm(true);
  };

  const blocId = editingBloc?.id || 'temp_' + Date.now();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-orange-500/10 p-3 rounded-lg">
            <Mic className="h-6 w-6 text-orange-500" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Expression Orale</h2>
            <p className="text-sm text-muted-foreground">{blocs.length} bloc(s) • 3 tâches par bloc • 12 min par examen</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setEditingBloc(null); resetForm(); }}
          data-testid="create-eo-bloc-btn"
          className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Créer un nouveau bloc</span>
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-card p-6 rounded-xl border shadow-sm animate-in">
          <h3 className="text-xl font-semibold mb-4">
            {editingBloc ? `Modifier ${editingBloc.name}` : 'Nouveau Bloc - Expression Orale'}
          </h3>
          
          <div className="space-y-6">
            {/* Bloc Name */}
            <div>
              <label className="text-sm font-medium">Nom du bloc</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                placeholder="Ex: Bloc 1 - Sujets quotidiens"
              />
            </div>

            {/* Tâches */}
            <div className="space-y-4">
              <TacheForm 
                tacheNum={1} 
                tacheKey="tache1" 
                formData={formData}
                setFormData={setFormData}
                fileInputRef={fileInputRefs.tache1}
                handleAudioUpload={handleAudioUpload}
                handleGenerateTTS={handleGenerateTTS}
                generatingTTS={generatingTTS}
                blocId={blocId}
              />
              <TacheForm 
                tacheNum={2} 
                tacheKey="tache2" 
                formData={formData}
                setFormData={setFormData}
                fileInputRef={fileInputRefs.tache2}
                handleAudioUpload={handleAudioUpload}
                handleGenerateTTS={handleGenerateTTS}
                generatingTTS={generatingTTS}
                blocId={blocId}
              />
              <TacheForm 
                tacheNum={3} 
                tacheKey="tache3" 
                formData={formData}
                setFormData={setFormData}
                fileInputRef={fileInputRefs.tache3}
                handleAudioUpload={handleAudioUpload}
                handleGenerateTTS={handleGenerateTTS}
                generatingTTS={generatingTTS}
                blocId={blocId}
              />
            </div>

            {/* Submit */}
            <div className="flex space-x-3 pt-4">
              <button
                onClick={editingBloc ? handleUpdateBloc : handleCreateBloc}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold"
              >
                <Save className="h-5 w-5" />
                <span>{editingBloc ? 'Mettre à jour' : 'Créer le bloc'}</span>
              </button>
              <button
                onClick={() => { setShowForm(false); setEditingBloc(null); resetForm(); }}
                className="px-6 py-3 rounded-lg border hover:bg-secondary transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blocs List */}
      <div className="space-y-4">
        <h3 className="font-semibold">Blocs d'exercices</h3>
        
        {blocs.length === 0 ? (
          <div className="bg-card p-8 rounded-xl border text-center">
            <Mic className="h-16 w-16 text-orange-500 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Aucun bloc créé</p>
            <p className="text-sm text-muted-foreground mt-2">Cliquez sur "Créer un nouveau bloc" pour commencer</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {blocs.map((bloc) => (
              <div key={bloc.id} className="bg-card rounded-xl border shadow-sm overflow-hidden">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                      <span className="text-orange-600 font-bold text-lg">{bloc.number}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold">{bloc.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        3 tâches • Créé le {new Date(bloc.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setExpandedBloc(expandedBloc === bloc.id ? null : bloc.id)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    >
                      {expandedBloc === bloc.id ? <ChevronUp className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={() => handleEditBloc(bloc)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors text-blue-500"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteBloc(bloc.id)}
                      className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-red-500"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {expandedBloc === bloc.id && (
                  <div className="border-t p-4 bg-secondary/10 space-y-3">
                    {(bloc.tasks || bloc.taches || []).map((tache) => (
                      <div key={tache.tache} className="bg-background p-3 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-orange-600 dark:text-orange-400">
                            Tâche {tache.tache} - {tacheTypes[tache.tache]}
                          </span>
                          <span className="text-xs text-muted-foreground">{tache.duree} min {tache.dureeSecondes || 0}s</span>
                        </div>
                        <p className="text-sm font-medium">{tache.titre || 'Sans titre'}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tache.instruction}</p>
                        {(tache.modeleAudioUrl || tache.modeleTexte) && (
                          <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                              ✓ Modèle audio disponible
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
